import React from "react";
import { Flag } from "lucide-react";

/**
 * RACE LOGIC v2.1 - real-time Racing Position Screen.
 * Horizontal progress bars below the racing screen (YOU / opponents / ghosts),
 * matching the owner's reference screenshots (green fill, name left, % right).
 * `racers` = [{ name, progress (0..1), isPlayer, tier }]
 */
const TIER_COLOR = { rookie: "#5C5B56", pro: "#9A9A96", elite: "#C7F94A", player: "#39FF14" };
const BE = process.env.REACT_APP_BACKEND_URL;
const avatarSrc = (u) => (u ? (u.startsWith("http") ? u : `${BE}${u}`) : null);

const RacerDot = ({ racer }) => (
  <span className="w-5 h-5 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-[9px] font-heading"
        style={{ background: racer.isPlayer ? "#39FF14" : TIER_COLOR[racer.tier] || "#52525b", color: "#0b0b0b" }}>
    {racer.avatar
      ? <img src={avatarSrc(racer.avatar)} alt="" className="w-full h-full object-cover" />
      : (racer.isPlayer ? "Y" : (racer.name || "R").slice(0, 1).toUpperCase())}
  </span>
);

export default function RacePositions({ racers = [] }) {
  const ordered = [...racers].sort((a, b) => (b.progress || 0) - (a.progress || 0));
  return (
    <div className="w-full bg-race-surface border border-race-line rounded-md p-2.5" data-testid="race-positions">
      <div className="overline text-race-sub mb-1.5">Live Positions</div>
      <div className="flex flex-col gap-1.5">
        {ordered.map((r, i) => {
          const pct = Math.round(Math.min(1, r.progress || 0) * 100);
          const done = pct >= 100;
          return (
            <div key={`${r.name}-${i}`} data-testid={`position-row-${r.isPlayer ? "you" : r.name}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="flex items-center gap-1.5 min-w-0">
                  <RacerDot racer={r} />
                  <span className={`text-[11px] font-mono uppercase tracking-wide truncate ${r.isPlayer ? "text-race-red font-bold" : "text-race-sub"}`}>
                    {r.isPlayer ? "YOU" : r.name}
                  </span>
                </span>
                <span className={`text-[10px] font-mono tabular ${done ? "text-race-volt" : "text-race-muted"}`}>{pct}%</span>
              </div>
              <div className="relative h-3 bg-race-bg border border-race-line rounded-full overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out"
                  style={{
                    width: `${pct}%`,
                    background: r.isPlayer
                      ? "linear-gradient(90deg,#2BB300,#39FF14)"
                      : TIER_COLOR[r.tier] || "#52525b",
                    boxShadow: r.isPlayer ? "0 0 12px rgba(57,255,20,0.55)" : "none",
                  }}
                />
                {done && <Flag size={10} className="absolute right-1 top-1/2 -translate-y-1/2 text-black/70" />}
              </div>
            </div>
          );
        })}
        {ordered.length === 0 && <p className="text-race-sub text-xs">Waiting for the field…</p>}
      </div>
    </div>
  );
}
